import React, { createContext, useContext, useState, useEffect, ReactNode } from "react"
import { ordersService } from "../services/firebase"
import toast from "react-hot-toast"

interface Notification {
  id: string
  type: "new_order" | "order_update" | "low_stock" | "system"
  title: string
  message: string
  timestamp: Date
  read: boolean
  data?: any
}

interface NotificationContextType {
  notifications: Notification[]
  unreadCount: number
  addNotification: (notification: Omit<Notification, "id" | "timestamp" | "read">) => void
  markAsRead: (id: string) => void
  markAllAsRead: () => void
  clearNotification: (id: string) => void
  clearAll: () => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

export const useNotifications = () => {
  const context = useContext(NotificationContext)
  if (!context) throw new Error("useNotifications must be used within a NotificationProvider")
  return context
}

interface NotificationProviderProps {
  children: ReactNode
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [lastOrderCount, setLastOrderCount] = useState<number>(0)
  const [isAdmin, setIsAdmin] = useState<boolean>(false)

  useEffect(() => {
    try {
      const raw = localStorage.getItem("persist:root")
      if (!raw) return
      const parsed = JSON.parse(raw)
      const authState = parsed?.auth ? JSON.parse(parsed.auth) : null
      const role = authState?.user?.role
      setIsAdmin(role === "admin")
    } catch {
      setIsAdmin(false)
    }
  }, [])

  const addNotification = (notification: Omit<Notification, "id" | "timestamp" | "read">) => {
    if (notification.type === "new_order" && !isAdmin) return

    const newNotification: Notification = {
      ...notification,
      id: Date.now().toString(),
      timestamp: new Date(),
      read: false,
    }

    setNotifications((prev) => [newNotification, ...prev].slice(0, 50))

    if (notification.type === "new_order" && isAdmin) {
      toast.success(notification.title, { duration: 4000, icon: "🛒" })
    }
  }

  const markAsRead = (id: string) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)))
  }

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
  }

  const clearNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id))
  }

  const clearAll = () => setNotifications([])

  useEffect(() => {
    if (!isAdmin) return

    const checkForNewOrders = async () => {
      try {
        const orders = await ordersService.getOrders()
        const currentOrderCount = orders.length

        if (lastOrderCount > 0 && currentOrderCount > lastOrderCount) {
          const newOrdersCount = currentOrderCount - lastOrderCount
          const latestOrders = orders
            .slice(0, newOrdersCount)
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

          latestOrders.forEach((order) => {
            addNotification({
              type: "new_order",
              title: "New Order Received!",
              message: `Order #${order.id.slice(-8)} for Rs ${order.total.toLocaleString()} from ${order.shippingAddress?.name || "Customer"}`,
              data: { orderId: order.id, total: order.total, customerName: order.shippingAddress?.name },
            })
          })
        }

        setLastOrderCount(currentOrderCount)
      } catch (error) {
        console.error("Error checking for new orders:", error)
      }
    }

    checkForNewOrders()
    const interval = setInterval(checkForNewOrders, 30000)
    return () => clearInterval(interval)
  }, [lastOrderCount, isAdmin])

  const unreadCount = notifications.filter((n) => !n.read).length

  return (
    <NotificationContext.Provider
      value={{ notifications, unreadCount, addNotification, markAsRead, markAllAsRead, clearNotification, clearAll }}
    >
      {children}
    </NotificationContext.Provider>
  )
}
